"use client";

import React, {useState} from "react";
import {useTranslation} from "react-i18next";
import Button from "@/components/library/Button";
import {SmsProviderSettings, SmsProviderType} from "@/types/settings";
import {
   type DirectoSmsFormErrors,
   type DirectoSmsFormValues,
   directoValuesFromProvider,
   EMPTY_DIRECTO_VALUES,
   EMPTY_QUICKLY_VALUES,
   EMPTY_TWILIO_VALUES,
   type QuicklySmsFormErrors,
   type QuicklySmsFormValues,
   quicklyValuesFromProvider,
   type TwilioSmsFormErrors,
   type TwilioSmsFormValues,
   twilioValuesFromProvider,
   validateDirecto,
   validateQuickly,
   validateTwilio,
} from "@/utils/integrations/smsValidation";
import ProviderTypePicker from "./ProviderTypePicker";
import DirectoSmsFields from "./DirectoSmsFields";
import QuicklySmsFields from "./QuicklySmsFields";
import TwilioSmsFields from "./TwilioSmsFields";

export interface SmsProviderFormProps {
   /** Stored provider config when editing; null when creating. */
   initialProvider: SmsProviderSettings | null;
   onSubmit: (provider: SmsProviderSettings) => Promise<void>;
   onCancel?: () => void;
}

/** Curried change handler: updates one field and clears its pending error. */
const makeFieldHandler =
   <T extends object>(
      setter: React.Dispatch<React.SetStateAction<T>>,
      errorSetter: React.Dispatch<React.SetStateAction<Partial<Record<keyof T, string>>>>
   ) =>
   (field: keyof T) =>
   (e: React.ChangeEvent<HTMLInputElement>) => {
      const next = e.target.value;
      setter((current) => ({...current, [field]: next}));
      errorSetter((current) => ({...current, [field]: undefined}));
   };

export default function SmsProviderForm({initialProvider, onSubmit, onCancel}: SmsProviderFormProps) {
   const {t} = useTranslation("partners");
   const {t: ct} = useTranslation("common");
   const [providerType, setProviderType] = useState<SmsProviderType>(
      initialProvider?.provider ?? "twilio"
   );
   const [twilio, setTwilio] = useState<TwilioSmsFormValues>(
      initialProvider?.provider === "twilio" ? twilioValuesFromProvider(initialProvider) : EMPTY_TWILIO_VALUES
   );
   const [quickly, setQuickly] = useState<QuicklySmsFormValues>(
      initialProvider?.provider === "quickly"
         ? quicklyValuesFromProvider(initialProvider)
         : EMPTY_QUICKLY_VALUES
   );
   const [directo, setDirecto] = useState<DirectoSmsFormValues>(
      initialProvider?.provider === "directo"
         ? directoValuesFromProvider(initialProvider)
         : EMPTY_DIRECTO_VALUES
   );
   const [twilioErrors, setTwilioErrors] = useState<TwilioSmsFormErrors>({});
   const [quicklyErrors, setQuicklyErrors] = useState<QuicklySmsFormErrors>({});
   const [directoErrors, setDirectoErrors] = useState<DirectoSmsFormErrors>({});
   const [submitting, setSubmitting] = useState(false);

   // Blank secrets mean "keep the stored value" (Galactus preserves null secret
   // fields on update), but only when the provider type is unchanged — switching
   // providers always requires fresh credentials.
   const secretOptional = initialProvider?.provider === providerType;

   const buildPayload = (): SmsProviderSettings | null => {
      if (providerType === "twilio") {
         const errors = validateTwilio(twilio, !secretOptional, t);
         setTwilioErrors(errors);
         if (Object.keys(errors).length > 0) return null;
         return {
            provider: "twilio",
            phoneNumber: twilio.phoneNumber.trim(),
            accountSid: twilio.accountSid.trim(),
            messagingServiceSid: twilio.messagingServiceSid.trim(),
            authToken: twilio.authToken.trim() ? twilio.authToken.trim() : null,
         };
      }
      if (providerType === "quickly") {
         const errors = validateQuickly(quickly, !secretOptional, t);
         setQuicklyErrors(errors);
         if (Object.keys(errors).length > 0) return null;
         return {
            provider: "quickly",
            url: quickly.url.trim(),
            apiKey: quickly.apiKey.trim() ? quickly.apiKey.trim() : null,
            apiSecret: quickly.apiSecret.trim() ? quickly.apiSecret.trim() : null,
         };
      }
      const errors = validateDirecto(directo, t);
      setDirectoErrors(errors);
      if (Object.keys(errors).length > 0) return null;
      return {provider: "directo", fromPhoneNumber: directo.fromPhoneNumber.trim()};
   };

   const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault();
      if (submitting) return;
      const payload = buildPayload();
      if (!payload) return;
      setSubmitting(true);
      try {
         await onSubmit(payload);
      } finally {
         setSubmitting(false);
      }
   };

   return (
      <form onSubmit={handleSubmit} className="space-y-4" data-testid="sms-provider-form">
         <ProviderTypePicker value={providerType} onChange={setProviderType} />
         {providerType === "twilio" && (
            <TwilioSmsFields
               values={twilio}
               errors={twilioErrors}
               secretOptional={secretOptional}
               onChange={makeFieldHandler(setTwilio, setTwilioErrors)}
            />
         )}
         {providerType === "quickly" && (
            <QuicklySmsFields
               values={quickly}
               errors={quicklyErrors}
               secretOptional={secretOptional}
               onChange={makeFieldHandler(setQuickly, setQuicklyErrors)}
            />
         )}
         {providerType === "directo" && (
            <DirectoSmsFields
               values={directo}
               errors={directoErrors}
               onChange={makeFieldHandler(setDirecto, setDirectoErrors)}
            />
         )}
         <div className="flex justify-end gap-3 pt-2">
            {onCancel && (
               <Button type="secondary" onClick={onCancel} disabled={submitting}>
                  {ct("cancel")}
               </Button>
            )}
            <Button type="primary" buttonType="submit" disabled={submitting}>
               {ct("save")}
            </Button>
         </div>
      </form>
   );
}
